import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EMPTY,
  NEVER,
  Subject,
  catchError,
  combineLatest,
  mergeMap,
  of,
  tap,
  throwError,
} from 'rxjs';
import { SOCKET_DRAWER_EVENT } from '../../enums/events-buffer/socket/socket-drawer-event.enum';
import { SOCKET_ROOM } from '../../enums/events-buffer/socket/socket-rooms-enum';
import { Logger } from '../../interfaces/Logger';
import { IDrawer } from '../../interfaces/drawer.interface';
import { SocketErrorResponse } from '../../interfaces/socket-error.model';
import { ISocketPayload } from '../../interfaces/socket-payload.interface';
import { SocketRoomHandlerService } from '../../services/socket/socket-room-handler.service';
import { SocketService } from '../../services/socket/socket.service';
import { SignatureService } from '../service/signature.service';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { DrawerMapper } from '@features/service/infrastructure/mappers/drawer.mapper';
import { ServiceDrawerAddedEvent } from '@platform/event-bus/domain/events/service-drawer.event';
import { EVENT_BUS } from '@shared/domain/tokens/event-bus.token';
import { ServiceDrawerRemovedEvent } from '@platform/event-bus/domain/events/service-drawer.event';

@Injectable({
  providedIn: 'root',
})
export class DrawerSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly signatureService = inject(SignatureService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed$: Subject<void> = new Subject<void>();

  private readonly eventBus = inject(EVENT_BUS);
  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);
  private readonly drawerMapper = inject(DrawerMapper);

  constructor() {
    this.drawerRoomListener();
  }

  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  /**
   * Listens to the drawer room entry
   *
   * @private
   * @memberof DrawerSocketHandlerService
   */
  private drawerRoomListener(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.drawers)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.logger.debug(
            `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.drawers} 💰] [Room joined, clearing previous subscriptions before reinitializing]`
          );
          this.clearSubscriptions();
        })
      )
      .subscribe(() => {
        this.initializeDrawerListeners();
      });
  }

  /**
   * Initializes all drawer room listeners
   *
   * @private
   * @memberof DrawerSocketHandlerService
   */
  private initializeDrawerListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.drawers} 🗃️] [Drawer listeners initialized]`
    );
    this.onDrawerAddedListener();
    this.onDrawerRemovedListener();
  }

  /**
   * Listens to the socket event that is activated when a drawer has been added
   *
   * @private
   * @memberof DrawerSocketHandlerService
   */
  private onDrawerAddedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.drawers} 🗃️] [Drawer added listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_DRAWER_EVENT.added)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const drawers = eventResponse.payload.cash_drawers as IDrawer[];
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (drawers?.length) {
              const drawersDomain = this.drawerMapper.fromLegacyApiArrayToDomain(drawers);

              return combineLatest([
                signature$,
                of(this.serviceStateService.insertDrawers(drawersDomain)),
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.drawers} 🗃️] [Drawer added socket recieved]`
        );
        this.eventBus.publish(new ServiceDrawerAddedEvent());
      });
  }

  private onDrawerRemovedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.drawers} 🗃️] [Drawer removed listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_DRAWER_EVENT.removed)
      .pipe(
        catchError((error) => throwError(() => error)),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest({
            eventResponse: of(eventResponse),
            currentSignature: this.signatureService.getSignatureFromIndexedDB(),
            currentDrawer: of(this.serviceStateService.getCurrentDrawer()),
          })
        ),
        mergeMap(({ eventResponse, currentSignature, currentDrawer }) => {
          //const drawer = eventResponse.payload.drawer as IDrawer;
          const signature = eventResponse.payload?.signature?.checksum;

          if (!currentDrawer) {
            this.logger.error(new Error('Error fetching current drawer'));

            return EMPTY;
          }

          if (signature && signature === currentSignature) {
            return NEVER;
          }

          let signature$ = of('');

          if (signature) {
            signature$ = this.signatureService.insertSignatureOnDB(signature);
          }

          /*const insertCurrentDrawerOnDB$ =
            this.drawerService.insertCurrentDrawerIntoIndexedDB(currentDrawer);
          const removeDrawerFromDrawers$ =
            this.drawerService.removeDrawer(drawer);

          if (drawer?._id === currentDrawer?.id) {
            currentDrawer.activated = false;

            return combineLatest([
              insertCurrentDrawerOnDB$,
              removeDrawerFromDrawers$,
              signature$,
            ]).pipe(
              tap(() => this.drawerService.notifyOnCurrentDrawerRemoved())
            );
          }*/
          this.serviceStateService.removeCurrentDrawer();

          /*return combineLatest([
            this.drawerService.removeDrawer(drawer),
            signature$,
          ]);*/
          return signature$;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.drawers} 🗃️] [Drawer removed from service]`
        );

        this.eventBus.publish(new ServiceDrawerRemovedEvent());
      });
  }
}
